import React                from 'react';
import { Link }             from 'react-router-dom';
import Modal                from "react-bootstrap/Modal";
import DatePicker           from "react-datepicker";

import * as reduxTypes      from 'utils/redux';

import consts               from 'shared/consts';
import * as OrderType       from 'shared/types/Order';
import * as VehicleType     from 'shared/types/Vehicle';
import * as UserType        from 'shared/types/User';
import * as ManifestType    from 'shared/types/Manifest';
import * as WSPayloadType   from 'shared/types/WSPayload';
import * as RouteType       from 'shared/types/Route';
import * as AgencyType      from 'shared/types/Agency';
import rehash               from 'shared/utils/rehash';
import dayjs                from 'shared/utils/day-timezone';
import englishWeekdays      from 'shared/utils/englishWeekdays';
import * as hhmmUtil        from 'shared/utils/hhmm';
import hashById             from 'shared/utils/hashById';

import Alert                from 'utils/Alert';
import getApiPromise        from 'utils/getApiPromise';
import usStates             from 'utils/usStates';
import tectransit           from 'utils/TecTransit';
import * as googleMapsLinks from 'utils/googleMapsLinks';
import * as images          from 'images/';
import * as MenuItem        from 'components/MenuItem';

interface VehicleProps {
    fixedRoutesIds  : string[];
    driversById     : Record<number,UserType.User>;
    vehicle         : WSPayloadType.Vehicle.Hydrated;
    orders          : OrderType.Hydrated<OrderType.Order<number>>[];
    onDeleteDriver  : (driver_id : number) => any;
    onAddDriver     : (driver_id : number) => any;
    onUpdate        : (delta  : Partial<VehicleType.Vehicle>) => any;
    onArchive?      : () => any;
    onCreate?       : () => any;
}
interface VehicleState {
    driversDom?         : any;
}
class JustVehicle extends React.Component<VehicleProps,VehicleState> {
    private unsubscribe? : ()=>void;
    componentDidMount() {
        const getState = () => {
            const scheduleByDriverId  = tectransit.agency.schedule_by_driver_id;
            const existingDrivers     = AgencyType.findDriverIds(scheduleByDriverId,(driverId,weekday,range) => {
                    return (range[2]===this.props.vehicle._id) ? range : undefined;
                }).map( driverId => {
                    return this.props.driversById[driverId];
                }).filter( driver => {
                    return !!driver;
                });
            const potentialDrivers              = Object.values(this.props.driversById).filter(pd=>!existingDrivers.some(ed=>(ed._id===pd._id)));
            const getDriverRangesByVehicleId    = ( driverSchedule:AgencyType.DriverSchedule ) => {
                return Object.values(driverSchedule).reduce( (acc,ranges) => {
                    return ranges.reduce((acc,range) => {
                        acc[range[2]] = range;
                        return acc;
                    },acc);
                },{} as Record<number,[number,number,number]>);
            }
            const potentialDriversDom = (
                <tr key={`${this.props.vehicle._id}_potential_drivers`}>
                    <td>
                        <select className="input-theme input-theme-min" id={`${this.props.vehicle._id}_potential_drivers`}>{potentialDrivers.map( d => {
                            const driverVehicleIds = Object.keys(getDriverRangesByVehicleId(scheduleByDriverId[d._id]||{}));
                            return (
                                <option key={d._id} value={d._id}>
                                    {d.name || d.email || `User #${d._id}`} ({driverVehicleIds.length>0 ? `driving vehicle(s) #${driverVehicleIds.join(',')}` : `not driving`})
                                </option>
                            );
                        })}
                        </select>
                    </td>
                    <td>
                        <images.AddDriver onClick={()=>{
                            const pd = (document.getElementById(`${this.props.vehicle._id}_potential_drivers`) as HTMLSelectElement);
                            return this.props.onAddDriver(pd ? Number(pd.value) : -1);
                        }}/>
                    </td>
                </tr>
            );
            const existingDriversDom  = existingDrivers.map( d => {
                const title = Object.entries(scheduleByDriverId[d._id]).map( ([weekday,ranges])=>{
                    return `${weekday} : `+ranges.filter(range=>(range[2]===this.props.vehicle._id)).map( range => {
                        return `${hhmmUtil.getHHMMFromMins(range[0])}-${hhmmUtil.getHHMMFromMins(range[1])}`;
                    }).join(',');
                }).join('\n');
                return (
                    <tr key={`${d._id}`}>
                        <td><Link title={title} to={`/Manager/Config/DriverSchedule/byVehicle/${this.props.vehicle._id}`}>{d.name || d.email || `Driver #${d._id}`}</Link></td> 
                        <td><images.DeleteDriver onClick={()=>this.props.onDeleteDriver(d._id)}/></td>
                    </tr>
                );
            });
            return {
                driversDom : (<table>
                    <tbody>
                        { potentialDrivers.length ? potentialDriversDom : (<tr key={"no_drivers_to_add"} className="nodriver" role="alert"><td colSpan={2}>No more drivers to add</td></tr>) }
                        { existingDrivers.length  ? existingDriversDom  : (<tr key={"no_drivers"} className="nodriver" role="alert"><td colSpan={2}>No drivers</td></tr>)  }
                    </tbody>
                </table>)
            };
        };
        this.setState(getState());
        this.unsubscribe = tectransit.store.subscribe(() => {
            this.setState(getState());
        });
    }
    componentWillUnmount() {
        if( this.unsubscribe ) {
            this.unsubscribe();
            delete this.unsubscribe;
        }
    }
    render() {
        if( !this.state?.driversDom )
            return (<></>);
        const odometer = Math.ceil((this.props.vehicle.odometer_meters||0)/consts.meters_in_mile);
        const reportedLocation  = this.props.vehicle.reported_location;
        const isAccuracyLow     = (reportedLocation?.accuracy||1000)>tectransit.agency.gps_accuracy_meters;
        const isLocationStale   = ((Date.now()/1000)-tectransit.agency.max_lateness_seconds)>(reportedLocation?.seconds||0);
        const vehItems = {
            'Fleet #'           : ((typeof this.props.onCreate==='function') ? (
                <>
                    <input
                        className   = "input-theme input-theme-min"
                        style       = {{width:'77%',display:'unset'}}
                        type        = "text"
                        defaultValue= {'(auto)'}
                        onChange    = {(e)=>this.props.onUpdate({_id:Number(e.target.value)})}
                    />
                </>) : (<>{this.props.vehicle._id}</>)),
            [tectransit.agency.unavailability_is_checkbox?'Unavailable':'Unavailability'] : (tectransit.agency.unavailability_is_checkbox ? (
                <input
                    type        = "checkbox"
                    name        = "unavailability_reason"
                    defaultChecked={this.props.vehicle.unavailability_reason!==''}
                    onChange    = {(e:React.ChangeEvent<HTMLInputElement>)=>this.props.onUpdate({unavailability_reason:e.target.checked?`unavailable as of ${dayjs().tz(tectransit.agency.time_zone).format(tectransit.timeFormat)}`:''})}
                />
            ) : (
                <input
                    type        = "text"
                    className   = "input-theme input-theme-min"
                    defaultValue= {this.props.vehicle.unavailability_reason||''}
                    size        = {50}
                    maxLength   = {90}
                    onBlur      = {(e)=>this.props.onUpdate({unavailability_reason:e.target.value.trim()})}/>
            )),                
            'Status'            : (<>
                <strong>{this.props.vehicle.is_online ? 'Online' : 'Offline'}</strong> since <strong>{dayjs(this.props.vehicle.is_online_changed_at).tz(tectransit.agency.time_zone).format(tectransit.timeFormat)}</strong>
                </>),
            'Location'          : reportedLocation
                ? <>{googleMapsLinks.getPlace(reportedLocation,undefined)}, accuracy is <span style={{color:(isAccuracyLow?'#ff0000':'#6c757d')}}>{reportedLocation.accuracy?.toFixed()??'n/a'}m</span> as of <span style={{color:(isLocationStale?'#ff0000':'#000000')}}>{dayjs(reportedLocation.seconds*1000).tz(tectransit.agency.time_zone).format(tectransit.timeFormat)}</span></>
                : 'n/a',
            'Drivers'           : this.state.driversDom,
            'Depot'             : (
                <select
                    className   ="input-theme input-theme-min"
                    defaultValue={this.props.vehicle.depot_name||''}
                    onChange    ={(e)=>this.props.onUpdate({depot_name:e.target.value.trim()})}>
                    {tectransit.agency.depots.map((d,ndx)=><option key={ndx} value={d.name}>{d.name}</option>)}
                </select>
            ),
            'Odometer'          : (
                <>
                    <input
                        className   = "input-theme input-theme-min"
                        style       = {{width:'77%',display:'unset'}}
                        type        = "number"
                        min         = {odometer/2}
                        max         = {odometer*2}
                        defaultValue= {odometer}
                        onChange    = {(e)=>this.props.onUpdate({odometer_meters:Number(e.target.value)*consts.meters_in_mile})}
                    />&nbsp;miles
                </>
            )
        } as Record<string,any>;
        if( this.props.vehicle.archived_at )
            vehItems['Archived At'] = (<>{dayjs(this.props.vehicle.archived_at).tz(tectransit.agency.time_zone).format(tectransit.timeFormat)}</>);
        if( (this.props.vehicle.type||'Bus')==='Bus' ) {
            Object.assign(vehItems,{
                [`Seating+Standing Positions`] : (
                    <input
                        type        = "text"
                        className   = "input-theme input-theme-min"
                        placeholder = "max number of seating positions"
                        defaultValue= {this.props.vehicle.seats||0}
                        size        = {3}
                        maxLength   = {3}
                        onBlur      = {(e)=>this.props.onUpdate({seats:Number(e.target.value.trim())})}/>
                ),
                'Wheelchair Positions' : (
                    <input
                        type        = "text"
                        className   = "input-theme input-theme-min"
                        placeholder = "max number of wheelchair positions"
                        defaultValue= {this.props.vehicle.wheelchairs||0}
                        size        = {3}
                        maxLength   = {3}
                        onBlur      = {(e)=>this.props.onUpdate({wheelchairs:Number(e.target.value.trim())})}/>
                ),
                'License Plate'  : (
                    <input
                        type        = "text"
                        placeholder = "license plate"
                        className   = "input-theme input-theme-min"
                        defaultValue= {this.props.vehicle.license_plate||''}
                        size        = {10}
                        maxLength   = {10}
                        onBlur      = {(e)=>this.props.onUpdate({license_plate:e.target.value.trim()})}/>
                ),
                'License State' : (
                    <select
                        defaultValue= {this.props.vehicle.license_state||'CA'}
                        className   = "input-theme input-theme-min"
                        onChange    = {(e)=>this.props.onUpdate({license_state:e.target.value.trim()})}>
                        {usStates.map(s=><option key={s} value={s}>{s}</option>)}
                    </select>
                ),
                'Make, Model & Year' : (
                    <input
                        type        = "text"
                        placeholder = "make, model, year"
                        className   = "input-theme input-theme-min"
                        defaultValue= {this.props.vehicle.make_model_year||''}
                        size        = {25}
                        maxLength   = {25}
                        onBlur      = {(e)=>this.props.onUpdate({make_model_year:e.target.value.trim()})}/>
                ),
                'VIN number' : (
                    <input
                        type        = "text"
                        className   = "input-theme input-theme-min"
                        defaultValue= {this.props.vehicle.vin_number||''}
                        size        = {25}
                        maxLength   = {25}
                        onBlur      = {(e)=>this.props.onUpdate({vin_number:e.target.value.trim()})}/>
                ),
                'Registration Expiry' : (
                    <DatePicker
                        className   = "input-theme input-theme-min"
                        selected    = {new Date(this.props.vehicle.registration_expires_at||Date.now())}
                        onChange    = {(date:Date|null) => {
                            this.props.onUpdate({registration_expires_at:date?.valueOf()});
                        }}
                        showTimeSelect={false}
                        dateFormat  ="yyyy-MM-dd"
                        placeholderText="Registration expiry date"
                    />
                ),
            });
            if( tectransit.agency.require_contract_id ) {
                vehItems['Contract ID'] = (
                    <input
                        type        = "text"
                        className   = "input-theme input-theme-min"
                        defaultValue= {this.props.vehicle.contract_id||''}
                        size        = {25}
                        maxLength   = {25}
                        onBlur      = {(e)=>this.props.onUpdate({contract_id:e.target.value.trim()})}/>
                )
            }
            if( tectransit.agency.on_demand_enabled ) {
                vehItems["Orders"]    = this.props.orders.map(order=>`#${order._id}`).join(', ')||'(none)';
                vehItems['On Demand'] = (
                    <input
                        type        = "checkbox"
                        name        = "on_demand_enabled"
                        defaultChecked={this.props.vehicle.on_demand_enabled}
                        onChange    = {(e:React.ChangeEvent<HTMLInputElement>)=>this.props.onUpdate({on_demand_enabled:e.target.checked})}
                    />
                );
            }
            if( tectransit.agency.fixed_route_enabled ) {
                vehItems['Fixed Route Name'] = (
                    <select
                        title       = 'Used only if the fixed route name is not defined by Manifest'
                        className   = "input-theme input-theme-min"
                        defaultValue= {this.props.vehicle.fixed_route_id}
                        onChange    = {(e)=>this.props.onUpdate({fixed_route_id:e.target.value.trim()})}>
                        {<option value="">(none)</option>}
                        {this.props.fixedRoutesIds.map((frId,ndx) =><option key={ndx} value={frId}>{frId}</option>)}
                    </select>
                );
            }
        }
        return [
            Object.entries(vehItems).map(([name,dom],ndx) => {
                return (<div key={ndx} id={`vehicle_${this.props.vehicle._id}_${name.replace(/[^a-z0-9_]/gi,'_').toLowerCase()}`}className="veh-item">
                    <div className="veh-name">{name}</div>
                    <div className="veh-content">{dom}</div>
                </div>);
            }),
            (typeof this.props.onArchive==='function') && (<div key={'archive'} className="veh-item">
                <div className="veh-name"></div>
                <div className="veh-content">
                    <button
                        className   = "btn btn-theme btn-min btn-delete"
                        onClick     = {(e)=>{
                            if( window.confirm(`Do you really want to archive vehicle #${this.props.vehicle._id}? If you go ahead then you will have to contact technical support to restore the vehicle.`) )
                                this.props.onArchive!();
                        }}>
                        Archive
                    </button>
                </div>
            </div>),
            (typeof this.props.onCreate==='function') && (<div key={'create'} className="veh-item">
                <div className="veh-name"></div>
                <div className="veh-content">
                    <button
                        className   = "btn btn-theme btn-min"
                        onClick     = {(e)=>this.props.onCreate!()}>
                        Create
                    </button>
                </div>
            </div>),
            <div key={`$divider_${this.props.vehicle._id}`} className="veh-end"/>
        ];
    }
}

interface Props {
}
interface State {
    wsPayload           : WSPayloadType.WSPayload.Hydrated;
    driversById         : Record<number,UserType.User>;
    fixedRouteIds       : string[];
    manifestsByVid      : Record<number,ManifestType.VehicleManifest.Hydrated<OrderType.Order<string>>>;
    ordersByVid         : Record<number,OrderType.Hydrated<OrderType.Order<number>>[]>;
    reassignedVid       : number;
    reassignedDelta     : (Partial<VehicleType.Vehicle>|undefined);
}
export default class Vehicles extends React.Component<Props,State> {
    private alert : Alert = new Alert();
    private newVehicle : VehicleType.Vehicle = {
        odometer_meters     : 0,
        seats               : 0,
        wheelchairs         : 0,
        depot_name          : (tectransit.agency.depots[0]?.name||''),
        license_state       : 'CA',
        unavailability_reason : '',
    } as VehicleType.Vehicle;
    private setScheduleByDriverId( scheduleByDriverId:Record<string,AgencyType.DriverSchedule> ) {
        const payload = {
            schedule_by_driver_id : scheduleByDriverId
        };
        return getApiPromise('/api/manager/agency','PUT',payload).then( res => {
            if( !res || res.err )
                throw Error(res?.err||`result is empty`);
            tectransit.store.dispatch({
                type    : reduxTypes.AGENCY__UPDATE,
                payload
            });
        }).catch( err => {
            this.alert.set(`Cannot set agency schedule (${err.message})`);
        });
    }
    private onAddVehicleDriver( vehicle:WSPayloadType.Vehicle.Hydrated, driver_id:number ) {
        const driver = this.state.driversById[driver_id];
        if( !driver ) {
            this.alert.set(`Driver #${driver_id} is not found`);
            return;
        }
        if( tectransit.agency.require_contract_id && vehicle.contract_id ) {
            if( !window.confirm(
                    `You are about to change the driver of vehicle #${vehicle._id} to '${driver.name}'. `+
                    `A change of a vehicle driver typically means a change of the vehicle contract. `+
                    `The current contract of the vehicle is to '${vehicle.contract_id}'.\n`+
                    `\n`+
                    `Press OK if '${vehicle.contract_id}' contract is still valid for the new driver. If it is not then change the contract first.` )
            ) {
                this.alert.set(`Driver change is cancelled`);
                return;
            }
        }
        // Just assign to this driver the same 24x7 schedule
        return this.setScheduleByDriverId({
            // Get a new schedule where this vehicle is completely removed
            ...AgencyType.filterDriverSchedule(tectransit.agency.schedule_by_driver_id,(driverId,weekday,range) => {
                return range[2]!==vehicle._id ? range : undefined;
            }),
            // Add a new schedule for this driver with the vehicle
            [driver._id] : englishWeekdays.reduce( (acc,wd,wdNdx) => {
                // Assign the driver to every minute the agency is open
                acc[wd.toLowerCase()] = tectransit.agency.workminutes[wdNdx].map( range => {
                    return [range[0],range[1],vehicle._id];
                });
                return acc;
            },{} as AgencyType.DriverSchedule)
        });
    }
    private onDeleteVehicleDriver( vehicle:WSPayloadType.Vehicle.Hydrated, driver_id:number ) {
        const driver = this.state.driversById[driver_id];
        if( !driver ) {
            this.alert.set(`Driver #${driver_id} is not found`);
            return;
        }
        return this.setScheduleByDriverId({
            // Get a new schedule where this vehicle is completely removed from this specifc driver
            ...AgencyType.filterDriverSchedule(tectransit.agency.schedule_by_driver_id,(driverId,weekday,range) => {
                return ((driverId===driver._id) && (range[2]!==vehicle._id)) ? range : undefined;
            })
        });
    }
    private onSaveVehicle( vehicle:WSPayloadType.Vehicle.Hydrated, delta:Partial<VehicleType.Vehicle>, query:Record<string,any>={} ) {
        return getApiPromise('/api/manager/vehicle','PUT',delta,{id:vehicle._id,...query})
            .then( res => {
                if( !res || res.err )
                    throw Error(res.err||'result is empty');
                if( Array.isArray(res.messages) )
                    this.alert.set(res.messages.join(';'));
                const newVehicle = {
                    ...vehicle,
                    ...delta
                };
                this.setState({
                    wsPayload : {
                        ...this.state.wsPayload,
                        vehiclesById : {
                            ...this.state.wsPayload.vehiclesById,
                            [vehicle._id] : newVehicle
                        }
                    },
                    reassignedVid       : -1,
                    reassignedDelta     : undefined
                });
                if( newVehicle.archived_at ) {
                    // If vehicle is archived then throw out all references to it from
                    // tectransit.agency.schedule_by_driver_id
                    this.setScheduleByDriverId(AgencyType.filterDriverSchedule(tectransit.agency.schedule_by_driver_id,(driverId,weekday,range) => {
                        return (range[2]===vehicle._id) ? undefined : range;
                    }));
                }
                return res;
            })
            .catch( err => {
                this.alert.set(`Cannot save vehicle (${err.message})`);
            });
    }
    private onUpdateVehicle( vehicle:WSPayloadType.Vehicle.Hydrated, delta:Partial<VehicleType.Vehicle> ) {
        try {
            if( !(vehicle._id in this.state.wsPayload.vehiclesById) )
                throw Error(`Cannot find vehicle with id #${vehicle._id}`);
            // Just do confirmations. All real input validation will happen on the server
            if( (delta.unavailability_reason||'').trim()!=='' || delta.archived_at ) {
                if( vehicle.fixed_route_id ) {
                    if( RouteType.getMeters(vehicle.route)>tectransit.agency.gps_accuracy_meters ) {
                        // This is a fixed route vehicle that is currently assigned to a trip
                        // Making it unavailable or archived requires a trip reassignment
                        this.setState({
                            reassignedVid   : vehicle._id,
                            reassignedDelta : delta
                        });
                        return;
                    }
                }
                else {
                    if( (this.state.ordersByVid[vehicle._id]||[]).length>0 ) {
                        // This is an on-demand vehicle that has some orders assigned to it.
                        // Making it unavailable or archived requires order re-assignment.
                        this.setState({
                            reassignedVid   : vehicle._id,
                            reassignedDelta : delta
                        });
                        return;
                    }
                }
            }
            return this.onSaveVehicle(vehicle,delta);
        }
        catch( err ) {
            this.alert.set(`Setting is invalid (${(err as Error).message})`);
        }
    }
    private onCreateVehicle( vehicle:VehicleType.Vehicle ) {
        return getApiPromise<VehicleType.Vehicle>('/api/manager/vehicle','POST',vehicle)
            .then( newVehicle => {
                if( !newVehicle || newVehicle.err )
                    throw Error(newVehicle.err||'result is empty');
                this.setState({
                    wsPayload : {
                        ...this.state.wsPayload,
                        vehiclesById : {
                            ...this.state.wsPayload.vehiclesById,
                            [newVehicle._id] : newVehicle
                        }
                    }
                });
                if( newVehicle._id!==vehicle._id ) {
                    // If vehicle id has changed, then replace all references to old _id with the new_id
                    // in tectransit.agency.schedule_by_driver_id
                    this.setScheduleByDriverId(AgencyType.filterDriverSchedule(
                        tectransit.agency.schedule_by_driver_id,
                        (driverId,weekday,range) => {
                            return [range[0],range[1],(range[2]===vehicle._id)?newVehicle._id:range[2]]
                        }
                    ));
                }
                return newVehicle;
            })
            .catch( err => {
                this.alert.set(`Cannot create vehicle (${err.message})`);
            });
    }
    private getReassignRouteModal( vehicle:WSPayloadType.Vehicle.Hydrated, delta:Partial<VehicleType.Vehicle> ) {
        const unavailabilityByVid  = {} as Record<number,string>;
        const substitutionVehicles = Object.values(this.state.wsPayload.vehiclesById)
            .filter( v => {
                if( v._id===vehicle._id ) {
                    unavailabilityByVid[v._id] = `Cannot substitute itself`
                    return false;
                }
                if( v.unavailability_reason ) {
                    unavailabilityByVid[v._id] = `Unavailable (${v.unavailability_reason})`;
                    return false;
                }
                if( v.archived_at ) {
                    unavailabilityByVid[v._id] = `Archived`;
                    return false;
                }
                if( !v.reported_location && !tectransit.agency.simulate_reported_location ) {
                    unavailabilityByVid[v._id] = `Current location is unknown`;
                    return false;
                }
                if( vehicle.fixed_route_id ) {
                    if( !v.fixed_route_id ) {
                        unavailabilityByVid[v._id] = `Not a fixed-route vehicle`;
                        return false;
                    }
                    if( RouteType.getMeters(v.route)>tectransit.agency.gps_accuracy_meters ) {
                        unavailabilityByVid[v._id] = `Already running route '${v.fixed_route_trip?.name||'??'}'`;
                        return false;
                    }
                }
                else {
                    if( v.fixed_route_id ) {
                        unavailabilityByVid[v._id] = `Not an on-demand vehicle`;
                        return false;
                    }
                }
                return true;
            })
            .sort( (v1,v2) => {
                // Sort them so that the vehicles with the fewest orders are at the top
                return (this.state.ordersByVid[v1._id]||[]).length-(this.state.ordersByVid[v2._id]||[]).length;
            });
        const unavailabilityTitle = Object.entries(unavailabilityByVid)
            .map(([vid,unavailability]) =>{
                return `#${vid}: ${unavailability}`;
            })
            .join('\n');
        if( vehicle.fixed_route_id ) {
            // This is a fixed-route vehicle
            return (
                <Modal
                    centered        = {true}
                    dialogClassName = "modal-90w"
                    aria-labelledby = "order-modal"
                    show            = {true}
                    onEntered       = {(modal) => {
                        window.scrollTo(0,0);
                    }}
                    onHide          = {() => {
                        this.setState({
                            reassignedVid   : -1,
                            reassignedDelta : undefined
                        });
                    }}
                >
                    <Modal.Header closeButton>
                        <Modal.Title>Fixed Route Trip of vehicle #{vehicle._id}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p>
                            Vehicle #{vehicle._id} now has <strong>{vehicle.fixed_route_trip?.name||'n/a'}</strong> route assigned to it.
                        </p>
                        { (substitutionVehicles.length>0) ? (
                            <React.Fragment>
                                <p>
                                    There are <strong title={unavailabilityTitle}>{substitutionVehicles.length}</strong> vehicle(s)
                                    that can potentially substitute vehicle #<strong>{vehicle._id}</strong>:
                                </p>
                                <select
                                    id          = "substitutionVehicleId"
                                    className   = "input-theme input-theme-min"
                                >
                                    {substitutionVehicles.map( v => {
                                        const vm            = this.state.manifestsByVid[v._id];
                                        return (<option key={v._id} value={v._id}>Vehicle #{v._id} (on {v.fixed_route_id},{vm?'has VM':'no VM'})</option>);
                                    })}
                                </select>
                                <button
                                    className   = "btn-theme btn-small"
                                    style       = {{margin:'auto'}}
                                    onClick     = {(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        const substitutionVehicleId = parseInt((document.getElementById('substitutionVehicleId') as HTMLSelectElement).value||'-1');
                                        return this.onSaveVehicle(vehicle,delta,{substitutionVehicleId});
                                    }}
                                >
                                    Substitute vehicle #{vehicle._id}
                                </button>
                                <p>
                                    Alternatively you can save your changes without substituting the vehicle for <b>{vehicle.fixed_route_trip?.name}</b>.
                                    If you proceed, <b> {vehicle.fixed_route_trip?.name} </b> will become abandoned.
                                </p>
                                <button
                                    className   = "btn-theme btn-small"
                                    style       = {{margin:'auto'}}
                                    onClick     = {(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        if( !window.confirm(`You have ${substitutionVehicles.length} vehicle(s) that can substite vehicle #${vehicle._id}. Do you really want to leave the orders unassigned?`) )
                                            return;
                                        return this.onSaveVehicle(vehicle,delta);
                                    }}
                                >
                                    Leave <b> {vehicle.fixed_route_trip?.name} </b> abandoned.
                                </button>
                            </React.Fragment>
                        ) : (
                            <React.Fragment>
                                <p>
                                    There are <strong title={unavailabilityTitle}>no</strong> vehicles that can substitute
                                    vehicle #<strong>{vehicle._id}</strong>. You can still save your changes by leaving
                                    route <b> {vehicle.fixed_route_trip?.name} </b> abandoned.
                                </p>
                                <button
                                    className   = "btn-theme btn-small"
                                    style       = {{margin:'auto'}}
                                    onClick     = {(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        return this.onSaveVehicle(vehicle,delta);
                                    }}
                                >
                                    Leave&nbsp;<b>{vehicle.fixed_route_trip?.name}</b>&nbsp;abandoned.
                                </button>
                            </React.Fragment>
                        ) }
                    </Modal.Body>
                </Modal>
            );
        }
        else {
            // This is an on-demand vehicle
            const vehicleOrders = this.state.ordersByVid[vehicle._id]||[];
            return (
                <Modal
                    centered        = {true}
                    dialogClassName = "modal-90w"
                    aria-labelledby = "order-modal"
                    show            = {true}
                    onEntered       = {(modal) => {
                        window.scrollTo(0,0);
                    }}
                    onHide          = {() => {
                        this.setState({
                            reassignedVid   : -1,
                            reassignedDelta : undefined
                        });
                    }}
                >
                    <Modal.Header closeButton>
                        <Modal.Title>Orders of vehicle #{vehicle._id}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p>
                            Vehicle #{vehicle._id} has <strong>{vehicleOrders.length}</strong> orders assigned to it.
                        </p>
                        { (vehicleOrders.length>0) && (
                            <table width="100%">
                                <thead>
                                    <tr>
                                        <td><strong>#</strong></td>
                                        <td><strong>Passenger</strong></td>
                                        <td><strong>Status</strong></td>
                                    </tr>
                                </thead>
                                <tbody>
                                    {vehicleOrders.map( order => {
                                        return (
                                            <tr key={order._id}>
                                                <td>{order._id}</td>
                                                <td><Link to={`/Dispatcher/User/${order.user_id}`}>{order.user?.name}</Link></td>
                                                <td>{order.status}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        ) }
                        { (substitutionVehicles.length>0) ? (
                            <React.Fragment>
                                <p>
                                    There are <strong title={unavailabilityTitle}>{substitutionVehicles.length}</strong> vehicle(s)
                                    that can potentially substitute vehicle #<strong>{vehicle._id}</strong>:
                                </p>
                                <select
                                    id          = "substitutionVehicleId"
                                    className   = "input-theme input-theme-min"
                                >
                                    {substitutionVehicles.map( v => {
                                        const vehicleOrders = this.state.ordersByVid[v._id]||[];
                                        const vm            = this.state.manifestsByVid[v._id];
                                        return (<option key={v._id} value={v._id}>Vehicle #{v._id} ({vehicleOrders.length} order(s),{vm?'has VM':'no VM'})</option>);
                                    })}
                                </select>
                                <button
                                    className   = "btn-theme btn-small"
                                    style       = {{margin:'auto'}}
                                    onClick     = {(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        const substitutionVehicleId = parseInt((document.getElementById('substitutionVehicleId') as HTMLSelectElement).value||'-1');
                                        return this.onSaveVehicle(vehicle,delta,{substitutionVehicleId});
                                    }}
                                >
                                    Substitute vehicle #{vehicle._id}
                                </button>
                                <p>
                                    Alternatively you can save your changes without substituting the vehicle for these orders.
                                    If you proceed, you will leave these orders <Link to={`/Dispatcher/Orders/Unassigned`}>unassigned
                                    </Link> and will have to manually re-assign the orders one-by-one using the <Link to={`/Dispatcher/Routes`}>Routes
                                    </Link> page.
                                </p>
                                <button
                                    className   = "btn-theme btn-small"
                                    style       = {{margin:'auto'}}
                                    onClick     = {(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        if( !window.confirm(`You have ${substitutionVehicles.length} vehicle(s) that can substite vehicle #${vehicle._id}. Do you really want to leave the orders unassigned?`) )
                                            return;
                                        return this.onSaveVehicle(vehicle,delta);
                                    }}
                                >
                                    Leave {vehicleOrders.length} order(s) unassigned
                                </button>
                            </React.Fragment>
                        ) : (
                            <React.Fragment>
                                <p>
                                    There are <strong title={unavailabilityTitle}>no</strong> vehicles that can substitute
                                    vehicle #<strong>{vehicle._id}</strong>. You can still save your changes by leaving these
                                    orders <Link to={`/Dispatcher/Orders/Unassigned`}>unassigned</Link>. If you proceed, you will
                                    have to manually re-assign the orders one-by-one using the <Link to={`/Dispatcher/Routes`}>Routes
                                    </Link> page.
                                </p>
                                <button
                                    className   = "btn-theme btn-small"
                                    style       = {{margin:'auto'}}
                                    onClick     = {(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        return this.onSaveVehicle(vehicle,delta);
                                    }}
                                >
                                    Leave {vehicleOrders.length} order(s) unassigned
                                </button>
                            </React.Fragment>
                        ) }
                    </Modal.Body>
                </Modal>
            );
        }
    }
    constructor( props:Props ) {
        super(props);
        this.state = {
            wsPayload             : {
                seconds      : (Date.now()/1000),
                vehiclesById : {},
                ordersById   : {},
                usersById    : {}
            },
            driversById         : {},
            fixedRouteIds       : [],
            manifestsByVid      : {},
            ordersByVid         : {},
            reassignedVid       : -1,
            reassignedDelta     : undefined
        }
    }
    componentDidMount() {
        Promise.all([
            getApiPromise<WSPayloadType.WSPayload.Dehydrated>('/api/manager/vehicles','GET')
                .then( wsPayload => {
                    if( !wsPayload || wsPayload.err )
                        this.alert.set(`Cannot get vehicles (${wsPayload?.err||'unknown error'})`);
                    return WSPayloadType.WSPayload.hydrate(wsPayload);
                }),
            getApiPromise<UserType.Dehydrated[]>('/api/manager/users?inRoles=Driver')
                .then( users => {
                    if( !users || users.err )
                        this.alert.set(`Cannot get drivers (${users?.err||'unknown error'})`);
                    return hashById(users.map(UserType.hydrate)) as Record<number,UserType.User>;
                }),
            // Ask for on demand manifests only if on demand is enabled
            (tectransit.agency.on_demand_enabled ? getApiPromise<ManifestType.VehicleManifest.Dehydrated<string>[]>(`/api/dispatcher/ondemand/vehicleManifests`,"GET",undefined,{date:dayjs().tz(tectransit.agency.time_zone).format('YYYY-MM-DD')}) : Promise.resolve([]))
                .then( dehydratedVehicleManifests => {
                    if( !dehydratedVehicleManifests || dehydratedVehicleManifests.err )
                        throw Error(`Cannot get vehicle manifests (${dehydratedVehicleManifests?.err||'unknown error'})`);
                    return dehydratedVehicleManifests.reduce( (acc,vm) => {
                        const usersById = rehash(vm.usersById,user=>UserType.hydrate(user));
                        const hydrated  = ManifestType.VehicleManifest.hydrate(
                            vm,
                            rehash(vm.ordersById,order=>OrderType.hydrate(order,usersById)),
                            vm.fixedRouteStopsById||{}
                        );
                        acc[hydrated.vehicle_id!] = hydrated;
                        return acc;
                    },{} as Record<number,ManifestType.VehicleManifest.Hydrated<OrderType.Order<string>>>);
                }),
            // Ask for fixed routes only if they are enabled
            (tectransit.agency.fixed_route_enabled ? getApiPromise<string[]>('/api/public/fixedRoute/routeIds') : Promise.resolve([]))
                .then( fixedRouteIds => {
                    if( !fixedRouteIds || fixedRouteIds.err )
                        this.alert.set(`Cannot get fixed routes (${fixedRouteIds?.err||'unknown error'})`);
                    return fixedRouteIds;
                })
        ]).then( ([wsPayload,driversById,manifestsByVid,fixedRouteIds]) => {
            return this.setState({
                wsPayload,
                driversById,
                manifestsByVid,
                // In case of fixed route only the wsPayload will not have any orders. Therefore
                // `ordersById` will be empty and `this.onUpdateVehicle` will never set
                // `reassignedVid` in the state.
                // In other words in case of fixed route only everything will work as designed.
                ordersByVid: Object.values(wsPayload.vehiclesById).reduce( (acc,vehicle) => {
                    acc[vehicle._id] = Object.values(
                        (vehicle.route?.legs||[]).reduce((acc2,leg)=>{
                            acc2 = (leg.pickup.orders||[]).reduce((acc,order)=>{
                                acc[order._id!] = order;
                                return acc;
                            },acc2);
                            acc2 = (leg.dropoff.orders||[]).reduce((acc,order)=>{
                                acc[order._id!] = order;
                                return acc;
                            },acc2);
                            return acc2;
                        },{} as Record<number,OrderType.Hydrated<OrderType.Order<number>>>)
                    );
                    return acc;
                },{} as Record<number,OrderType.Hydrated<OrderType.Order<number>>[]>),
                fixedRouteIds
            });
        });
    }
    render() {
        return MenuItem.withMenuItem("Vehicles",( alert:Alert ) => {
            this.alert = alert;
            const allVehicles = Object.values(this.state.wsPayload.vehiclesById);
            const vehicles    = allVehicles.filter(v => !v.archived_at).sort((v1,v2)=>(v1._id-v2._id));
            return (
                <div className="content wrapper section-card top-1">
                    <div className="vehicles-innersection-content">
                        {vehicles.map( (v,ndx) => {
                            const vehicle = <JustVehicle
                                key             = {ndx}
                                fixedRoutesIds  = {this.state.fixedRouteIds}
                                driversById     = {this.state.driversById}
                                vehicle         = {v}
                                orders          = {this.state.ordersByVid[v._id]||[]}
                                onDeleteDriver  = {(driver_id)=>this.onDeleteVehicleDriver(v,driver_id)}
                                onAddDriver     = {(driver_id)=>this.onAddVehicleDriver(v,driver_id)}
                                onUpdate        = {(delta)=>this.onUpdateVehicle(v,delta)}
                                onArchive       = {()=>this.onUpdateVehicle(v,{archived_at:Date.now()})}
                            />;
                            if( this.state.reassignedVid!==v._id )
                                return vehicle;
                            return [
                                <React.Fragment key={`reassignedVidModal_${v._id}`}>{this.getReassignRouteModal(v,this.state.reassignedDelta||{})}</React.Fragment>,
                                vehicle
                            ];
                        })}
                        <JustVehicle
                            key             = {vehicles.length}
                            fixedRoutesIds  = {this.state.fixedRouteIds}
                            driversById     = {this.state.driversById}
                            vehicle         = {this.newVehicle}
                            orders          = {[]}
                            onDeleteDriver  = {(driver_id)=>this.onDeleteVehicleDriver(this.newVehicle,driver_id)}
                            onAddDriver     = {(driver_id)=>this.onAddVehicleDriver(this.newVehicle,driver_id)}
                            onUpdate        = {(delta)=>Object.assign(this.newVehicle,delta)}
                            onCreate        = {()=>this.onCreateVehicle(this.newVehicle)}
                        />
                    </div>
                    <div>
                        Agency has
                        <strong>{vehicles.filter(v=>!v.unavailability_reason).length}</strong> available vehicles,
                        <strong>{vehicles.filter(v=>!!v.unavailability_reason).length}</strong> unavailable vehicles
                        (and <strong>{allVehicles.length-vehicles.length}</strong> archived ones)
                    </div>
                </div>
            );
        });
    }
}
